import { useCallback, useEffect, useRef, useState } from "react";

// Minimal type for the non-standard beforeinstallprompt event (not in lib.dom).
type BeforeInstallPromptEvent = Event & {
  prompt: () => Promise<void>;
  userChoice: Promise<{ outcome: "accepted" | "dismissed" }>;
};

/**
 * Register the PWA service worker. No-op in dev (we never want to cache the Vite
 * dev server) and on browsers without service worker support. Registered at the
 * root scope so the worker can cache content-hashed /assets/* for offline, while
 * the worker itself enforces the multi-tenant cache safety bypasses.
 */
export function registerServiceWorker() {
  if (typeof navigator === "undefined" || !("serviceWorker" in navigator)) {
    return;
  }
  if (!import.meta.env.PROD) {
    return;
  }
  const register = () => {
    navigator.serviceWorker.register("/sw.js", { scope: "/" }).catch(() => {
      // Registration failure is non-fatal: the app still works online.
    });
  };
  // `/app` is client-rendered, so by the time this runs the window `load` event has
  // usually already fired — a `load` listener would never run. Register immediately
  // when the document is complete, otherwise wait for load.
  if (document.readyState === "complete") {
    register();
  } else {
    window.addEventListener("load", register, { once: true });
  }
}

type InstallPromptState = {
  canPrompt: boolean;
  installed: boolean;
  prompt: () => void;
};

/**
 * Track installability: captures `beforeinstallprompt` so the UI can offer an
 * in-app install button, and reflects the installed / standalone state. iOS has
 * no `beforeinstallprompt`, so `canPrompt` stays false there (the UI shows the
 * "Add to Home Screen" hint instead).
 */
export function useInstallPrompt(): InstallPromptState {
  const [canPrompt, setCanPrompt] = useState(false);
  const [installed, setInstalled] = useState(false);
  const deferredRef = useRef<BeforeInstallPromptEvent | null>(null);

  useEffect(() => {
    if (typeof window === "undefined") {
      return;
    }
    const standalone =
      window.matchMedia?.("(display-mode: standalone)").matches ||
      (window.navigator as Navigator & { standalone?: boolean }).standalone === true;
    if (standalone) {
      setInstalled(true);
    }

    const onBeforeInstallPrompt = (event: Event) => {
      event.preventDefault();
      deferredRef.current = event as BeforeInstallPromptEvent;
      setCanPrompt(true);
    };
    const onInstalled = () => {
      setInstalled(true);
      setCanPrompt(false);
      deferredRef.current = null;
    };

    window.addEventListener("beforeinstallprompt", onBeforeInstallPrompt);
    window.addEventListener("appinstalled", onInstalled);
    return () => {
      window.removeEventListener("beforeinstallprompt", onBeforeInstallPrompt);
      window.removeEventListener("appinstalled", onInstalled);
    };
  }, []);

  const prompt = useCallback(() => {
    const deferred = deferredRef.current;
    if (!deferred) {
      return;
    }
    void deferred.prompt();
    void deferred.userChoice.finally(() => {
      deferredRef.current = null;
      setCanPrompt(false);
    });
  }, []);

  return { canPrompt, installed, prompt };
}
